import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { friendlyError } from "@/lib/api";
import { ageRangeOptions } from "@/lib/labels";
import { seekersApi } from "@/lib/endpoints";
import { mapsAvailable, MapPicker } from "@/components/GoogleMaps";
import { LocationSelect } from "@/components/LocationSelect";
import { resolveGeocode } from "@/lib/venezuelaLocations";
import { PhoneInput } from "@/components/PhoneInput";
import { PhoneVerify } from "@/components/PhoneVerify";

export default function SeekerForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [photos, setPhotos] = useState([]); // {file, url}
  const [form, setForm] = useState({
    reporter_name: "", phone1: "", phone2: "", child_name: "", age_range: "",
    state: "", municipality: "", lat: "", lng: "", additional_info: "",
  });
  const [geoLoading, setGeoLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [idToken, setIdToken] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const next = [...photos];
    for (const f of files) {
      if (next.length >= 3) break;
      next.push({ file: f, url: URL.createObjectURL(f) });
    }
    setPhotos(next);
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const setPin = ({ lat, lng }) => setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }));

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error(t("seeker.toastGeoUnavailable")); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        setForm((f) => ({ ...f, lat: String(latitude), lng: String(longitude) }));
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=12&accept-language=es`);
        const data = await res.json();
        const a = data.address || {};
        const resolved = resolveGeocode(a.state, a.county || a.city || a.town || a.municipality || a.suburb || "");
        if (resolved.state) {
          setForm((f) => ({ ...f, state: resolved.state, municipality: resolved.municipality }));
          toast.success(resolved.municipality ? t("seeker.toastZoneDetected") : t("seeker.toastStateDetected"));
        } else {
          toast.message(t("seeker.toastSelectManual"));
        }
      } catch {
        toast.error(t("seeker.toastLocError"));
      } finally { setGeoLoading(false); }
    }, () => { toast.error(t("seeker.toastGeoDenied")); setGeoLoading(false); }, { timeout: 10000 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (photos.length === 0) { toast.error(t("seeker.toastNeedPhoto")); return; }
    for (const [k, fieldKey] of [["reporter_name", "fieldYourName"], ["phone1", "fieldPhone1"],
      ["child_name", "fieldChildName"], ["age_range", "fieldAge"], ["state", "fieldState"], ["municipality", "fieldMunicipality"]]) {
      if (!form[k]) { toast.error(t("seeker.toastMissing", { field: t(`seeker.${fieldKey}`) })); return; }
    }
    if (!consent) { toast.error(t("seeker.toastConsent")); return; }
    if (!idToken) { toast.error(t("seeker.toastVerify")); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("photos", p.file));
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("firebase_id_token", idToken);
      await seekersApi.create(fd);
      setDone(true);
      toast.success(t("seeker.toastPublished"));
    } catch (err) {
      toast.error(friendlyError(err, t("seeker.toastPublishError")));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center text-center gap-4 py-10" data-testid="seeker-success">
        <CheckCircle2 className="w-14 h-14 text-[#166534]" />
        <h1 className="font-heading text-2xl font-bold">{t("seeker.successTitle")}</h1>
        <p className="text-neutral-600 max-w-sm">
          {t("seeker.successText")}
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <button data-testid="go-search-btn" onClick={() => navigate("/")}
            className="bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[48px] font-semibold">{t("seeker.goSearch")}</button>
        </div>
      </div>
    );
  }

  const labelCls = "text-sm font-medium text-neutral-900 mb-1.5 block";
  const inputCls = "w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none";

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="seeker-form">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{t("seeker.title")}</h1>
        <p className="text-sm text-neutral-600 mt-1">{t("seeker.subtitle")}</p>
      </div>

      <div>
        <label className={labelCls}>{t("seeker.photosLabel")}</label>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" data-testid="seeker-photos-input" />
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <div key={p.url} className="relative aspect-square">
              <img src={p.url} alt={`foto ${i + 1}`} className="w-full h-full object-cover rounded-sm" />
              <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {photos.length < 3 && (
            <div onClick={() => fileRef.current?.click()} data-testid="seeker-photo-dropzone"
              className="aspect-square bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-sm flex flex-col items-center justify-center gap-1 cursor-pointer text-neutral-500">
              <Camera className="w-6 h-6" /><span className="text-xs">{t("seeker.addPhoto")}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className={labelCls}>{t("seeker.yourFullName")}</label>
        <input data-testid="seeker-reporter-name" className={inputCls} value={form.reporter_name} onChange={set("reporter_name")} />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className={labelCls}>{t("seeker.phone1")}</label>
          <PhoneVerify testid="seeker-verify" phone={form.phone1}
            onPhoneChange={(v) => setForm((f) => ({ ...f, phone1: v }))}
            verified={!!idToken} onVerified={setIdToken} onReset={() => setIdToken("")} />
        </div>
        <div>
          <label className={labelCls}>{t("seeker.phone2")}</label>
          <PhoneInput testid="seeker-phone2" value={form.phone2}
            onChange={(v) => setForm((f) => ({ ...f, phone2: v }))} placeholder={t("seeker.phone2Placeholder")} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{t("seeker.childName")}</label>
          <input data-testid="seeker-child-name" className={inputCls} value={form.child_name} onChange={set("child_name")} />
        </div>
        <div>
          <label className={labelCls}>{t("seeker.age")}</label>
          <select data-testid="seeker-age-range" className="native-select" value={form.age_range} onChange={set("age_range")}>
            <option value="">{t("common.select")}</option>
            {ageRangeOptions(t).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>{t("seeker.whereLost")}</label>
        <p className="text-xs text-neutral-500 mb-2">{t("seeker.whereLostHint")}</p>
        {mapsAvailable() && <div className="mb-2"><MapPicker lat={form.lat ? Number(form.lat) : null} lng={form.lng ? Number(form.lng) : null} onChange={setPin} /></div>}
        <LocationSelect state={form.state} municipality={form.municipality}
          onChange={({ state, municipality }) => setForm((f) => ({ ...f, state, municipality }))}
          onDetect={detectLocation} detecting={geoLoading} prefix="seeker-loc" />
        {form.lat && form.lng && <p className="text-xs text-[#166534] mt-1">{t("seeker.locMarked")}</p>}
      </div>

      <div>
        <label className={labelCls}>{t("seeker.additionalInfo")}</label>
        <input data-testid="seeker-additional-info" className={inputCls} value={form.additional_info} onChange={set("additional_info")}
          placeholder={t("seeker.additionalInfoPlaceholder")} />
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none border border-neutral-200 rounded-sm p-3 bg-neutral-50" data-testid="seeker-consent-label">
        <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0 accent-[#0066CC]" checked={consent}
          onChange={(e) => setConsent(e.target.checked)} data-testid="seeker-consent-checkbox" />
        <span className="text-sm text-neutral-700">{t("seeker.consent")}</span>
      </label>

      {!idToken && <p className="text-xs text-[#B45309]">{t("seeker.mustVerify")}</p>}
      <button type="submit" disabled={submitting || !consent || !idToken} data-testid="submit-seeker-btn"
        className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 disabled:cursor-not-allowed rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
        {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("seeker.submitting")}</> : t("seeker.submit")}
      </button>
    </form>
  );
}
